/*
 * UNIK size guide on the product page (F4-01).
 *
 * <unik-size-guide> (snippets/unik-size-guide.liquid) opens a native <dialog> with the
 * product brand's table from the theme-owned guide. prepareSizeGuide is ported unchanged
 * from the Shella reference (assets/unik-product-purchase.js): it accepts the content only
 * when the brand is one of the known guides and the markup has the expected structure
 * (one brand block, the right section, the expected number of tables); otherwise the
 * fallback (contact + full guide links) is shown.
 *
 * The size dialog (F2-03) opens the guide on top of itself via window.UnikSizeGuide.open,
 * so the size dialog is still there when the guide closes.
 */
(() => {
  if (customElements.get('unik-size-guide')) return;

  const guideKeys = new Set([
    'boheme', 'cream', 'decoy', 'dny', 'gozzip', 'ilse-jacobsen', 'jbs-of-denmark', 'kaffe', 'kaffe-curve',
    'm-jeans', 'mapp', 'marta-du-chateau', 'micha', 'mix-by-heart', 'only-carmakoma', 'sandgaard',
    'sister-soul', 'smashed-lemon', 'soulmate', 'studio', 'zizzi', 'zoey',
  ]);

  function prepareSizeGuide(template, fallback) {
    const reserve = () => ({ fragment: fallback.content.cloneNode(true), brand: '' });
    if (!template || !['page', 'product'].includes(template.dataset.unikSizeGuideType)) return reserve();
    const key = template.dataset.unikSizeGuideBrand;
    if (!guideKeys.has(key)) return reserve();
    const fragment = template.content.cloneNode(true);
    const roots = fragment.querySelectorAll('.unik-size-guide-content');
    if (roots.length !== 1) return reserve();
    const root = roots[0];
    const markers = Array.from(root.querySelectorAll('[data-unik-size-guide-brand]'));
    const matches = markers.filter((element) => element.dataset.unikSizeGuideBrand === key);
    if (matches.length !== 1) return reserve();
    const selected = matches[0].cloneNode(true);
    const section = template.dataset.unikSizeGuideSection || 'clothing';
    if (!['shoes', 'clothing'].includes(section)) return reserve();
    const sections = Array.from(selected.querySelectorAll('[data-unik-size-guide-section]'));
    if (sections.length || section === 'shoes' || key === 'ilse-jacobsen') {
      const matching = sections.filter((element) => element.dataset.unikSizeGuideSection === section);
      const panel = selected.querySelector(':scope > .unik-size-guide-content__panel');
      if (
        matching.length !== 1 ||
        !panel ||
        matching[0].parentElement !== panel ||
        !matching[0].matches('section[id]') ||
        matching[0].querySelector('[data-unik-size-guide-section]')
      )
        return reserve();
      matching[0].removeAttribute('id');
      panel.replaceChildren(matching[0]);
    }
    const brand = selected.querySelector(':scope > summary')?.textContent.trim();
    const tableCount = selected.querySelectorAll('table').length;
    const validTableCount = ['marta-du-chateau', 'mix-by-heart', 'sister-soul'].includes(key)
      ? tableCount === 0 && selected.querySelector('p')
      : tableCount === 1 ||
        (['cream', 'decoy', 'jbs-of-denmark', 'only-carmakoma'].includes(key) && tableCount === 2) ||
        (key === 'kaffe' && tableCount === 6);
    if (!selected.matches('details.unik-size-guide-content__brand') || !brand || !validTableCount || selected.querySelector('[data-unik-size-guide-brand]'))
      return reserve();
    const common =
      section === 'shoes'
        ? []
        : Array.from(root.children).filter((element) =>
            element.matches('.unik-size-guide-content__note, .unik-size-guide-content__measurements, .unik-size-guide-content__help')
          );
    if (common.some((element) => element.querySelector('table, [data-unik-size-guide-brand]'))) return reserve();
    // The dialog title already names the brand; show its content without an accordion.
    const brandContent = document.createElement('div');
    brandContent.className = selected.className;
    brandContent.dataset.unikSizeGuideBrand = key;
    brandContent.append(...Array.from(selected.childNodes).filter((node) => node.nodeName !== 'SUMMARY'));
    root.replaceChildren(brandContent, ...common);
    const wrapper = document.createElement('div');
    wrapper.className = 'rte';
    wrapper.append(root);
    fragment.replaceChildren(wrapper);
    return { fragment, brand };
  }

  class UnikSizeGuide extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-unik-size-guide-dialog]');
      if (!this.dialog || this.ready) return;
      this.ready = true;
      this.trigger = null;
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-unik-size-guide-open]')) this.open(event.target.closest('button'));
        else if (event.target.closest('[data-unik-size-guide-close]')) this.dialog.close();
      });
      this.dialog.addEventListener('close', () => {
        const trigger = this.trigger;
        this.trigger = null;
        if (trigger?.isConnected) trigger.focus();
      });
      // A press that starts and ends on the backdrop closes the dialog.
      let backdropPress = false;
      const outside = (event) => {
        const box = this.dialog.getBoundingClientRect();
        return event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom;
      };
      this.dialog.addEventListener('pointerdown', (event) => (backdropPress = event.target === this.dialog && outside(event)));
      this.dialog.addEventListener('click', (event) => {
        if (backdropPress && event.target === this.dialog && outside(event)) this.dialog.close();
        backdropPress = false;
      });
    }

    /** The product's extracted "Mål" (unik-product-description) in the same product-info, or null. */
    productMeasurements() {
      const scope = this.closest('product-info') || document;
      const source = scope.querySelector('[data-product-measurements]');
      return source?.children.length ? source : null;
    }

    /** Opens the guide; focus returns to `trigger` (or the active element) when it closes. */
    open(trigger) {
      if (!this.dialog || this.dialog.open) return;
      const prepared = prepareSizeGuide(
        this.querySelector(':scope > template[data-unik-size-guide-content]'),
        this.querySelector(':scope > template[data-unik-size-guide-fallback]')
      );
      const title = prepared.brand
        ? (this.dialog.dataset.brandTitle || '').replace('__brand__', prepared.brand)
        : this.dialog.dataset.title || '';
      const heading = document.createElement('h2');
      heading.className = 'unik-size-guide-content__dialog-title';
      heading.textContent = title;
      prepared.fragment.querySelector('.unik-size-guide-content')?.prepend(heading);
      // The product's own measurements (28.10.2026) sit under the title, before the brand table or the fallback.
      const measurements = this.productMeasurements();
      if (measurements && heading.parentNode) {
        const section = document.createElement('section');
        section.className = 'unik-size-guide-content__product-measurements';
        const sectionTitle = document.createElement('h2');
        sectionTitle.textContent = this.dialog.dataset.measurementsTitle || '';
        section.setAttribute('aria-label', sectionTitle.textContent);
        section.append(sectionTitle, ...Array.from(measurements.childNodes, (node) => node.cloneNode(true)));
        heading.after(section);
      }
      this.dialog.setAttribute('aria-label', title);
      this.dialog.querySelector('[data-unik-size-guide-destination]').replaceChildren(prepared.fragment);
      this.trigger = trigger || document.activeElement;
      this.dialog.showModal();
      this.dialog.querySelector('[data-unik-size-guide-close]')?.focus();
    }
  }

  customElements.define('unik-size-guide', UnikSizeGuide);

  window.UnikSizeGuide = {
    prepareSizeGuide,
    /** Opens the size guide inside `root` (e.g. product-info). Returns false when there is none. */
    open(root, trigger) {
      const guide = root?.querySelector('unik-size-guide');
      if (!guide?.open) return false;
      guide.open(trigger);
      return true;
    },
  };
  if (typeof module === 'object' && module.exports) module.exports = { prepareSizeGuide };
})();
